/**
 * Note Editor - Modal for creating/editing notes
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { FieldLabel } from '../common/editorial/EditorialControls';

const NoteEditor = ({ note, user, onClose, onSave, linkedType, linkedId }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (note) {
      setTitle(note.title || '');
      setContent(note.content || '');
      setTags(note.tags || []);
    }
  }, [note]);

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      const noteData = {
        title: title.trim(),
        content: content.trim(),
        tags: tags,
        linked_type: linkedType || note?.linked_type,
        linked_id: linkedId || note?.linked_id,
      };

      if (note) {
        await axios.put(`/api/notes/${note.note_id}`, noteData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post('/api/notes/', noteData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="aura-card w-full max-w-3xl max-h-[90vh] flex flex-col light-surface"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-hairline">
            <h2 className="font-display text-h2 text-primary">{note ? 'Edit note' : 'New note'}</h2>
            <button type="button" onClick={onClose} className="btn-ghost p-2" aria-label="Close">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-5">
            {error && (
              <div className="surface-panel p-3 border border-danger/40">
                <p className="text-body text-danger">{error}</p>
              </div>
            )}

            <div>
              <FieldLabel htmlFor="note-title">Title</FieldLabel>
              <input
                id="note-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Note title…"
                className="input-editorial"
              />
            </div>

            <div>
              <FieldLabel htmlFor="note-content">Content</FieldLabel>
              <textarea
                id="note-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your note…"
                rows={12}
                className="input-editorial resize-none"
              />
            </div>

            <div>
              <FieldLabel>Tags</FieldLabel>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 text-label px-2 py-1 border border-hairline rounded-md text-secondary bg-pine-tint"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-faint hover:text-primary"
                      aria-label={`Remove ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag…"
                  className="input-editorial flex-1"
                />
                <button type="button" onClick={handleAddTag} className="btn-secondary">
                  Add
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 mt-4 border-t border-hairline">
            <button type="button" onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !title.trim() || !content.trim()}
              className="btn-primary disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default NoteEditor;
